import React, { useEffect, useRef } from 'react';
import { AccessibilityInfo, Animated, Easing, Platform, StyleSheet, Text, View } from 'react-native';
import { getGuideColors } from './GuideCardLayout';

const CARD_LABELS = {
  'gab-ui': 'interactive answer',
  recipe: 'recipe',
  workout: 'workout',
  mealplan: 'meal plan',
  checklist: 'checklist',
  itinerary: 'itinerary',
  comparison: 'comparison',
  studyplan: 'study plan',
  projectguide: 'project guide',
  eventplan: 'event plan',
  chart: 'chart',
  places: 'places',
};

const pulseStyles = `
  @keyframes gab-card-pulse { 0%, 100% { opacity: .5; } 50% { opacity: 1; } }
  [data-card-skeleton] { animation: gab-card-pulse 3.2s ease-in-out infinite; }
  @media (prefers-reduced-motion: reduce) { [data-card-skeleton] { animation: none; opacity: .75; } }
`;

function StreamingCardPlaceholder({ kind, colors: themeColors, fontScale = 1, embedded = false }) {
  const colors = getGuideColors(themeColors, kind);
  const label = kind === 'diagram' ? 'Generating diagram…' : kind === 'html' ? 'Generating your preview…' : `Preparing your ${CARD_LABELS[kind] || 'card'}…`;
  const opacity = useRef(new Animated.Value(0.5)).current;
  useEffect(() => {
    // CSS runs the web pulse without a JS frame loop. Native platforms use
    // the compositor, and both paths honor the system's motion preference.
    if (Platform.OS === 'web') return undefined;
    let active = true;
    let animation;
    const update = reduced => {
      if (!active) return;
      animation?.stop();
      opacity.setValue(reduced ? 0.75 : 0.5);
      if (reduced) return;
      const timing = toValue => Animated.timing(opacity, {
        toValue, duration: 1600, easing: Easing.inOut(Easing.ease), useNativeDriver: true, isInteraction: false,
      });
      animation = Animated.loop(Animated.sequence([timing(1), timing(0.5)]));
      animation.start();
    };
    AccessibilityInfo.isReduceMotionEnabled().then(update).catch(() => update(true));
    const listener = AccessibilityInfo.addEventListener('reduceMotionChanged', update);
    return () => { active = false; animation?.stop(); listener.remove(); };
  }, [opacity]);
  const Skeleton = Platform.OS === 'web' ? View : Animated.View;
  const barColor = kind === 'diagram'
    ? (themeColors.status_bar_style === 'dark' ? '#dedee3' : '#4b4b50')
    : (themeColors.status_bar_style === 'dark' ? '#eeeeef' : '#333336');
  const bar = (width, height = 10) => <View style={[styles.line, { width, height, backgroundColor: barColor }]} />;

  return (
    <View
      accessibilityRole="progressbar"
      accessibilityLabel={label}
      accessibilityState={{ busy: true }}
      aria-busy
      dataSet={{ streamingCard: kind, copyExclude: '' }}
      style={[styles.container, { backgroundColor: colors.background, borderColor: colors.border }, embedded && styles.embedded]}
    >
      {Platform.OS === 'web' && <style>{pulseStyles}</style>}
      <View accessible={false} importantForAccessibility="no-hide-descendants" aria-hidden>
        <Text style={[styles.label, { color: colors.secondary, fontSize: 13 * fontScale, lineHeight: 20 * fontScale }]}>{label}</Text>
      </View>
      <Skeleton dataSet={{ cardSkeleton: '' }} style={[styles.skeleton, Platform.OS !== 'web' && { opacity }]}
        accessible={false} accessibilityElementsHidden importantForAccessibility="no-hide-descendants" aria-hidden>
        {kind === 'diagram' ? <>
          <View dataSet={{ diagramSkeleton: '' }} style={[styles.diagram, { borderColor: colors.border }]}>
            <View style={[styles.connector, { left: '26%', top: '50%', width: '47%', height: 3, backgroundColor: barColor }]} />
            <View style={[styles.connector, { left: '73%', top: '25%', width: 3, height: '50%', backgroundColor: barColor }]} />
            {[{ left: '8%', top: '40%' }, { left: '62%', top: '15%' }, { left: '62%', top: '65%' }].map((position, index) =>
              <View key={index} style={[styles.diagramPart, position, { backgroundColor: barColor }]} />)}
            <View style={[styles.diagramJoint, { backgroundColor: barColor }]} />
          </View>
          <View style={styles.diagramChoices}>{[0, 1, 2].map(index => <View key={index} style={[styles.line, { width: '24%', height: 28, backgroundColor: barColor }]} />)}</View>
        </> : <>
        {bar('56%', 20)}
        <View style={styles.description}>{bar('92%')}{bar('72%')}</View>
        <View style={[styles.details, { borderColor: colors.border }]}>
          {[0, 1, 2].map(index => <View key={index} style={styles.detail}>{bar('65%', 12)}{bar('42%', 8)}</View>)}
        </View>
        </>}
      </Skeleton>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { width: '100%', maxWidth: '100%', minWidth: 0, borderWidth: 1, borderRadius: 24, padding: 24, marginVertical: 8 },
  embedded: { borderWidth: 0, borderRadius: 0, padding: 0, marginVertical: 0, backgroundColor: 'transparent' },
  label: { fontWeight: '500' },
  skeleton: { marginTop: 22 },
  line: { borderRadius: 6 },
  description: { marginTop: 18, gap: 10 },
  details: { flexDirection: 'row', marginTop: 24, paddingTop: 20, borderTopWidth: 1, gap: 16 },
  detail: { flex: 1, minWidth: 0, gap: 9 },
  diagram: { width: '100%', aspectRatio: 5 / 3, minHeight: 160, borderWidth: 1, borderRadius: 14, overflow: 'hidden' },
  connector: { position: 'absolute' },
  diagramPart: { position: 'absolute', width: '24%', height: '20%', borderRadius: 12 },
  diagramJoint: { position: 'absolute', left: '43%', top: '44%', width: '12%', height: '12%', borderRadius: 8 },
  diagramChoices: { flexDirection: 'row', gap: 8, marginTop: 16 },
});

export default React.memo(StreamingCardPlaceholder);
